import { clamp } from '../flight/Aerodynamics';
import { createControlInput, type ControlInput } from '../flight/FlightState';
import { Bindings, InputAction, type InputActionId } from './Bindings';
import { GamepadInput, isPadActionPressed, padToLook } from './Gamepad';
import { KeyboardMouse, createAxisSample, createLookCommand, type LookCommand } from './KeyboardMouse';

/**
 * Device aggregation and command filtering — step 1 of the per-tick system order (SPEC §4.3).
 *
 * Keyboard/mouse and gamepad contribute to the same axes and are summed then clamped, so a
 * player can hold the stick and nudge with a key without one silently winning. Throttle is
 * integrated here rather than in either device, because both of them command a *rate* and the
 * position has to live somewhere shared.
 *
 * `sample` is called once per tick with the fixed dt, so ramp and recentring rates are in
 * per-second units and behave identically regardless of frame rate.
 */

export class InputManager {
  readonly bindings: Bindings;
  readonly keyboardMouse: KeyboardMouse;
  readonly gamepad: GamepadInput;

  /** The filtered command the flight model consumes. Reused every tick; never reallocated. */
  readonly control: ControlInput = createControlInput();

  /** Camera look-around command for this tick. Reused; never reallocated. */
  readonly look: LookCommand = createLookCommand();

  private readonly keyboardAxes = createAxisSample();
  private readonly gamepadAxes = createAxisSample();
  private readonly padLook = createLookCommand();
  private throttle = 0;

  constructor(bindings: Bindings = Bindings.load()) {
    this.bindings = bindings;
    this.keyboardMouse = new KeyboardMouse(bindings);
    this.gamepad = new GamepadInput(bindings);
  }

  /** Initial throttle, 0..1 — set at spawn so the aircraft does not start at idle. */
  setThrottle(value: number): void {
    this.throttle = clamp(value, 0, 1);
    this.control.throttle = this.throttle;
  }

  get throttleValue(): number {
    return this.throttle;
  }

  attach(target: HTMLElement): void {
    this.keyboardMouse.attach(target);
  }

  detach(): void {
    this.keyboardMouse.detach();
  }

  /** Sample every device and produce this tick's command. dtS is the fixed timestep. */
  sample(dtS: number): ControlInput {
    this.gamepad.poll();

    // Look mode is decided before the axes are read, because it changes what the right stick
    // means: borrowed for the camera, it must stop commanding yaw and throttle.
    const padLookHeld = isPadActionPressed(
      this.gamepad.pad,
      this.bindings,
      InputAction.LookAround,
    );

    this.keyboardMouse.sample(dtS, this.keyboardAxes);
    this.gamepad.sample(this.gamepadAxes, padLookHeld);

    const control = this.control;
    control.rollCmd = clamp(this.keyboardAxes.roll + this.gamepadAxes.roll, -1, 1);
    control.pitchCmd = clamp(this.keyboardAxes.pitch + this.gamepadAxes.pitch, -1, 1);
    control.yawCmd = clamp(this.keyboardAxes.yaw + this.gamepadAxes.yaw, -1, 1);

    const throttleRate = this.keyboardAxes.throttleRate + this.gamepadAxes.throttleRate;
    this.throttle = clamp(this.throttle + throttleRate * dtS, 0, 1);
    control.throttle = this.throttle;

    return control;
  }

  /**
   * Sample the *view* controls, on the render clock rather than the tick.
   *
   * Look-around and zoom are presentation, not simulation: they never touch the flight model and
   * never enter the checksum. Driving them from the fixed tick froze the view whenever the debug
   * pause was on — while the mouse delta kept accumulating, so unpausing snapped the camera
   * across the whole cone at once.
   */
  sampleView(): LookCommand {
    this.gamepad.poll();
    const padLookHeld = isPadActionPressed(
      this.gamepad.pad,
      this.bindings,
      InputAction.LookAround,
    );
    this.updateLook(padLookHeld);
    return this.look;
  }

  /** Merge the mouse drag and the pad stick into one look command. */
  private updateLook(padLookHeld: boolean): void {
    this.keyboardMouse.sampleLook(this.look);
    if (!padLookHeld) return;
    padToLook(this.gamepad.pad, this.bindings, this.padLook);
    this.look.rateX = this.padLook.rateX;
    this.look.rateY = this.padLook.rateY;
    this.look.active = true;
  }

  /** True if a discrete action fired on any device since the last call. */
  consumeAction(action: InputActionId): boolean {
    const keyboard = this.keyboardMouse.consumeAction(action);
    const pad = this.gamepad.consumeAction(action);
    return keyboard || pad;
  }

  /** True while the action is held on any device. */
  isActionHeld(action: InputActionId): boolean {
    return (
      this.bindings.isActionPressed(action, this.heldKeys) ||
      isPadActionPressed(this.gamepad.pad, this.bindings, action)
    );
  }

  /** Drop every queued edge — used when the simulation is reset or the menu opens. */
  clearPendingActions(): void {
    this.keyboardMouse.clearPendingActions();
    this.gamepad.clearPendingActions();
  }

  /**
   * The keyboard's held-key set. KeyboardMouse owns it; exposing a read-only view here keeps
   * `isActionHeld` from needing a second copy each call.
   */
  private get heldKeys(): ReadonlySet<string> {
    return this.keyboardMouse.heldKeys;
  }
}
